import { Controller, useFormContext } from "react-hook-form";
import { ReceiptTemplateFormValues } from "../../components/ReceiptTemplateForm";
import {
  Button,
  Card,
  FlatActionList,
  Page,
  Stack,
  TextButton,
  TextField,
  Divider,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Switch, View } from "react-native";
import { useTranslation } from "react-i18next";
import { ReceiptTemplateFormParamList } from "../../../../types";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Colors } from "react-native-ui-lib";
import { useMutation } from "@apollo/client";
import {
  MUTATION_CREATE_RECEIPT_TEMPLATE,
  MUTATION_UPDATE_RECEIPT_TEMPLATE,
} from "../../common";
import { Receipt } from "@towersystems/roam-common/lib/generated-types";
import * as ImagePicker from "expo-image-picker";
import { Image } from "react-native";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";

export type ReceiptTemplateFormScreenProps = NativeStackScreenProps<
  ReceiptTemplateFormParamList,
  "ReceiptTemplateFormScreen"
>;

export const ReceiptTemplateFormScreen = ({
  navigation,
}: ReceiptTemplateFormScreenProps) => {
  const { control, setValue, formState, handleSubmit, watch } =
    useFormContext<ReceiptTemplateFormValues>();
  const id = watch("id");
  const flashMessagesContext = useFlashMessages();
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const title = id ? "Edit Receipt Template" : "Create Receipt Template";

  const { t } = useTranslation("settings");

  const [createReceiptFn] = useMutation<{ createReceipt: Receipt }>(
    MUTATION_CREATE_RECEIPT_TEMPLATE,
  );

  const [updateReceiptFn] = useMutation<{ updateReceipt: Receipt }>(
    MUTATION_UPDATE_RECEIPT_TEMPLATE,
  );

  const watchedTemplateStyle = watch("defaultTemplateType");
  const watchExistingLogo = watch("settings.existingLogoURL");

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const handleOnSubmit = (data: ReceiptTemplateFormValues) => {
    if (data.id) {
      updateReceiptFn({
        variables: {
          input: {
            id: data.id,
            name: data.name,
            enabled: data.enabled,
            defaultTemplateType: data.defaultTemplateType,
            settings: {
              header: data.settings.header,
              footer: data.settings.footer,
              labelTax: data.settings.labelTax,
              displayCustomerDetails: data.settings.displayCustomerDetails,
              logoBase64: data.settings.uploadLogo
                ? {
                    base64: data.settings.uploadLogo.base64,
                    imageName: data.settings.uploadLogo.imageName,
                  }
                : data.settings.existingLogoURL
                ? undefined
                : null,
            },
          },
        },
      })
        .then(async (res) => {
          if (res.data?.updateReceipt) {
            flashMessagesContext.showMessage({
              severity: "success",
              message: t("message.receipt_template_saved"),
            });
            try {
              await fetchAndSyncEntity("receipts");
            } catch (error) {
              console.log(error);
            }
            navigation.goBack();
          } else {
            throw new Error("Receipt template update failed");
          }
        })
        .catch((error) => {
          flashMessagesContext.showMessage({
            severity: "error",
            message: t("message.error_save_receipt_template"),
          });
        });
    } else {
      createReceiptFn({
        variables: {
          input: {
            name: data.name,
            enabled: data.enabled,

            defaultTemplateType: data.defaultTemplateType,
            settings: {
              header: data.settings.header,
              footer: data.settings.footer,
              labelTax: data.settings.labelTax,
              displayCustomerDetails: data.settings.displayCustomerDetails,
              logoBase64: data.settings.uploadLogo
                ? {
                    base64: data.settings.uploadLogo.base64,
                    imageName: data.settings.uploadLogo.imageName,
                  }
                : undefined,
            },
          },
        },
      })
        .then(async (res) => {
          if (res.data?.createReceipt) {
            flashMessagesContext.showMessage({
              severity: "success",
              message: t("message.receipt_template_saved"),
            });
            try {
              await fetchAndSyncEntity("receipts");
            } catch (error) {
              console.log(error);
            }
            navigation.goBack();
          } else {
            throw new Error("Receipt template creation failed");
          }
        })
        .catch((error) => {
          flashMessagesContext.showMessage({
            severity: "error",
            message: t("message.error_save_receipt_template"),
          });
        });
    }
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_docket_template")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title={title} footer={footerMarkup}>
      <Stack vertical>
        <Card title="General">
          <Stack vertical>
            <Controller
              control={control}
              name="name"
              render={({ field: { onChange, value } }) => (
                <TextField label="Name" value={value} onChangeText={onChange} />
              )}
            />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  title: "Template Style",
                  onPress: () =>
                    navigation.navigate("SelectTemplateStyleScreen"),
                  key: "select-template-style",
                  componentRight: () => {
                    return (
                      <TextButton
                        onPress={() =>
                          navigation.navigate("SelectTemplateStyleScreen")
                        }
                      >
                        {watchedTemplateStyle || "Set template style"}
                      </TextButton>
                    );
                  },
                },
              ]}
            />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "receipt_template_enabled",
                  title: t("form.receipt_template_enabled.label"),
                  subTitle: t("form.receipt_template_enabled.description"),
                  onPress: () => {},
                  componentRight: () => (
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
        <Card title="Logo">
          {watchExistingLogo ? (
            <View style={{ alignItems: "flex-start" }}>
              <Stack vertical>
                <Image
                  source={{ uri: watchExistingLogo }}
                  style={{ width: 100, height: 100, borderRadius: 10 }}
                />
                <View>
                  <Button
                    isDestructive
                    onPress={() => {
                      setValue("settings.existingLogoURL", "");
                    }}
                    label="Remove Existing Logo"
                  ></Button>
                </View>
              </Stack>
            </View>
          ) : (
            <Controller
              control={control}
              name="settings.uploadLogo"
              render={({ field: { value, onChange } }) => (
                <View style={{ alignItems: "flex-start" }}>
                  {!value && (
                    <Button
                      isAction
                      label={"Upload Logo"}
                      onPress={async () => {
                        const result =
                          await ImagePicker.launchImageLibraryAsync({
                            mediaTypes: ImagePicker.MediaTypeOptions.Images,
                            allowsEditing: false,
                            aspect: [1, 1],
                            quality: 0.7,
                            base64: true,
                          });
                        if (
                          !result.canceled &&
                          result.assets &&
                          result.assets.length > 0
                        ) {
                          const asset = result.assets[0];
                          const imageName =
                            asset.fileName ||
                            asset.uri?.split("/").pop() ||
                            "logo.jpg";
                          onChange({
                            uri: asset.uri,
                            base64: asset.base64,
                            imageName,
                          });
                        }
                      }}
                    ></Button>
                  )}
                  {value && (
                    <Stack vertical>
                      <Image
                        source={{ uri: value.uri }}
                        style={{ width: 100, height: 100, borderRadius: 10 }}
                      />

                      <Button
                        isDestructive
                        label="Remove Logo"
                        onPress={() => onChange(null)}
                      ></Button>
                    </Stack>
                  )}
                </View>
              )}
            />
          )}
        </Card>
        <Card title="Labels & Text">
          <Stack vertical>
            <Controller
              control={control}
              name="settings.header"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Header Text"
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="settings.footer"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Footer Text"
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="settings.labelTax"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Tax Label"
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
            <FlatActionList
              items={[
                {
                  key: "receipt_template_enabled",
                  title: t(
                    "form.receipt_template_display_customer_details.label",
                  ),
                  subTitle: t(
                    "form.receipt_template_display_customer_details.description",
                  ),
                  onPress: () => {},
                  componentRight: () => (
                    <Controller
                      name="settings.displayCustomerDetails"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};
